import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { useUserRoles } from '@anytag/auth';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { Anchor, TableCell, TableV2 } from '@anytag/shared-atoms';
import { useGenerateInfluencerProfileLink } from '@anytag/shared-hooks';
import {
  getOffset,
  formatPrice,
  formatPercent,
  formatIntNumber,
  convertOrderToORDER,
  getValuableVariables
} from '@anytag/shared-utils';
import {
  AutoManagedReportSummary,
  AutoManagedReportByInfluencer,
  useAutoManagedReportByInfluencerQuery,
  MarketplaceAffiliateReportByInfluencerField
} from '@anytag/graphql';
import { useAutoManagedReportFilter } from '../hooks';
import { formatAffiliatePeriod } from './utils';

interface AutoManagedReportByInfluencerRowData {
  className?: string;
  influencerInfo?: AutoManagedReportByInfluencer['influencerInfo'];
  stats?: Partial<Omit<AutoManagedReportByInfluencer, 'influencerInfo'>>;
}

interface AutoManagedReportByInfluencerTableProps {
  currency: string;
  campaignId: number;
  summaryData?: AutoManagedReportSummary;
}

export const AutoManagedReportByInfluencerTable = ({
  currency,
  campaignId,
  summaryData
}: AutoManagedReportByInfluencerTableProps) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();
  const {
    page,
    limit,
    sort,
    filter: { endDate, startDate }
  } = useAutoManagedReportFilter();
  const { data, loading } = useAutoManagedReportByInfluencerQuery({
    variables: getValuableVariables({
      limit,
      endDate,
      startDate,
      id: campaignId,
      offset: getOffset(page, limit),
      orderBy: { field: sort.field, order: convertOrderToORDER(sort.order) }
    })
  });

  const columns: ColumnDef<AutoManagedReportByInfluencerRowData>[] = [
    {
      accessorKey: 'influencerName',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Influencer Name'),
      cell: ({ row }) => {
        const { className, influencerInfo } = row.original;
        const profileLink = generateProfileLink(influencerInfo?.id);

        return className === 'total-row' ? (
          <TableCell.CampaignTotal
            title={
              <TableCell.Text
                value={formatAffiliatePeriod({
                  title: t('Total'),
                  period: { endDate: endDate || '', startDate: startDate || '' }
                })}
              />
            }
          />
        ) : (
          <div className="flex items-center gap-2">
            <InfluencerAvatar size="48px" src={influencerInfo?.thumbNail} id={influencerInfo?.id} />
            <Anchor lines={3} label={influencerInfo?.name} {...profileLink} />
          </div>
        );
      }
    },
    {
      accessorKey: 'clicks',
      header: t<string>('HeaderColumn.Click'),
      meta: { align: 'right', sortField: MarketplaceAffiliateReportByInfluencerField.CLICK },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.clicks)} />
    },
    {
      accessorKey: 'conversions',
      header: t<string>('HeaderColumn.Order'),
      meta: { align: 'right', sortField: MarketplaceAffiliateReportByInfluencerField.ORDER },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.conversions)} />
    },
    {
      accessorKey: 'cvr',
      header: t<string>('HeaderColumn.CVR'),
      meta: { align: 'right', sortField: MarketplaceAffiliateReportByInfluencerField.CVR },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.cvr, false)} unit="%" />
    },
    {
      accessorKey: 'sales',
      header: t<string>('HeaderColumn.Store Sales'),
      meta: { align: 'right', sortField: MarketplaceAffiliateReportByInfluencerField.SALE },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.sales || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'cost',
      header: t<string>('HeaderColumn.Cost'),
      meta: { align: 'right', sortField: MarketplaceAffiliateReportByInfluencerField.COST },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.cost || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'commissionRate',
      header: t<string>('HeaderColumn.Influencer Commission Rate'),
      meta: {
        align: 'right',
        sortField: MarketplaceAffiliateReportByInfluencerField.INFLUENCER_COMMISSION_RATE,
        hidden: !isAdminStaff
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.commissionRate, false)} unit="%" />
    },
    {
      accessorKey: 'influencerCost',
      header: t<string>('HeaderColumn.Influencer Commission'),
      meta: {
        align: 'right',
        sortField: MarketplaceAffiliateReportByInfluencerField.INFLUENCER_COST,
        hidden: !isAdminStaff
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.influencerCost || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'promotionMethods',
      header: t<string>('HeaderColumn.Promotion method'),
      cell: ({ row }) => <TableCell.Text value={row.original.stats?.promotionMethods?.join(', ') || '-'} />
    }
  ];

  const tableData: AutoManagedReportByInfluencerRowData[] = [
    { className: 'total-row', stats: summaryData },
    ...(data?.autoManagedReportByInfluencer?.map<AutoManagedReportByInfluencerRowData>(
      ({ influencerInfo, ...stats }) => ({ stats, influencerInfo })
    ) || [])
  ];

  return (
    <TableV2
      loading={loading}
      className="report-table"
      data={tableData}
      columns={columns}
      rowHeight="73px"
      pagination={{ totalRecords: data?.autoManagedReportByInfluencerCount?.totalNumber }}
      noTopBorderRadius
      stickyHeader
    />
  );
};
